import { useCallback, useRef, useEffect } from 'react';
import data from '@emoji-mart/data';
import Picker from '@emoji-mart/react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

interface EmojiData {
  id: string;
  name: string;
  native: string;
  unified: string;
  keywords: string[];
  shortcodes: string;
}

interface EmojiPickerProps {
  /** Триггер элемент для открытия пикера */
  children: React.ReactNode;
  /** Callback при выборе эмодзи */
  onEmojiSelect: (emoji: string) => void;
  /** Открыт ли пикер */
  open?: boolean;
  /** Callback при изменении состояния */
  onOpenChange?: (open: boolean) => void;
  /** Disabled состояние */
  disabled?: boolean;
}

export function EmojiPicker({
  children,
  onEmojiSelect,
  open,
  onOpenChange,
  disabled = false,
}: EmojiPickerProps) {
  const pickerRef = useRef<HTMLDivElement>(null);

  const handleSelect = useCallback((emoji: EmojiData) => {
    onEmojiSelect(emoji.native);
    onOpenChange?.(false);
  }, [onEmojiSelect, onOpenChange]);

  // Закрываем пикер при клике вне
  useEffect(() => {
    if (!open) {return;}

    const handleClickOutside = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
        onOpenChange?.(false);
      }
    };

    // Небольшая задержка чтобы не закрылось сразу при открытии
    const timeout = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timeout);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open, onOpenChange]);

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild disabled={disabled}>
        {children}
      </PopoverTrigger>
      <PopoverContent
        className="w-auto p-0 border-0"
        side="top"
        align="end"
        sideOffset={8}
      >
        <div ref={pickerRef}>
          <Picker
            data={data}
            onEmojiSelect={handleSelect}
            theme="light"
            locale="ru"
            previewPosition="none"
            skinTonePosition="none"
            navPosition="bottom"
            perLine={8}
            maxFrequentRows={2}
            emojiButtonSize={36}
            emojiSize={22}
            categories={[
              'frequent',
              'people',
              'nature',
              'foods',
              'activity',
              'places',
              'objects',
              'symbols',
              'flags',
            ]}
            i18n={{
              search: 'Поиск',
              search_no_results_1: 'Ничего не найдено',
              search_no_results_2: '',
              pick: 'Выберите эмодзи...',
              add_custom: 'Добавить эмодзи',
              categories: {
                activity: 'Активность',
                custom: 'Кастомные',
                flags: 'Флаги',
                foods: 'Еда и напитки',
                frequent: 'Часто используемые',
                nature: 'Животные и природа',
                objects: 'Объекты',
                people: 'Люди и лица',
                places: 'Путешествия и места',
                search: 'Результаты поиска',
                symbols: 'Символы',
              },
              skins: {
                choose: 'Выберите тон кожи',
                1: 'По умолчанию',
                2: 'Светлый',
                3: 'Средне-светлый',
                4: 'Средний',
                5: 'Средне-тёмный',
                6: 'Тёмный',
              },
            }}
          />
        </div>
      </PopoverContent>
    </Popover>
  );
}
